<template>
  <div>
    <page-header
      left-side-class="col-8"
      right-side-class="col-4 d-flex align-items-center"
      :page="headerConfig"
      :actions="headerActionsConfig"
    />

    <AddStudent
      v-if="showAddStudent"
      @on-added="() => filterSearchHandler()"
      @cancel="showAddStudent = $event"
    />

    <section-title
      has-line
      content="Búsqueda Estudiantes con Impedimentos Visuales"
    />
    <filter-manager
      v-model="filter.model"
      :filters="filterConfig"
      @search="filterSearchHandler"
    />
    <custom-separator
      class="my-4"
      text="Listado de Estudiantes con Impedimentos Visuales"
    />
    <custom-result-not-found v-if="itemsEmpty" />
    <div
      v-for="(item, i) of items"
      :key="`student-visual-impairment-${i}`"
      class="mx-3"
    >
      <result-row
        class="my-4"
        :data="item"
        @enter="onEnterHandler"
        @saved="onChangeStatusSaved"
      />
    </div>
    <pagination
      v-if="showPagination"
      :data="pagination"
      @pagination-go-page="goToPage"
    />
  </div>
</template>

<script>
import mixAlert from "@/mixin/alert.mixin";
import PageHeader from "PageHeader";
import FilterManager from "FilterManager";
import SectionTitle from "SectionTitle";
import CustomSeparator from "custom-separator";
import CustomResultNotFound from "custom-result-not-found";
import Pagination from "pagination";
import { ResultRow } from "./components";
import locationServices from "@/services/api/location.api";
import storeMixin from "@/mixin/storeGenerator";
import loadingMixin from "@quisit/mipe-utils/mixins/loading";
import { getStudentVisualImpairmentStatuses } from "@/modules/common/code.api";
import {
  getVisualImpairments,
  getVisualImpairmentsAcademicYears
} from "@/services/studentVisualImpairments.js";
import AddStudent from "./components/AddStudent";

const DEFAULT_PAGE_SIZE = 20;

export default {
  name: "studentsRegistrationVisualInpairmentsMain",
  mixins: [storeMixin, loadingMixin, mixAlert],
  components: {
    PageHeader,
    SectionTitle,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    ResultRow,
    AddStudent
  },
  data() {
    return {
      showAddStudent: false,
      results: [],
      filter: {
        options: {
          academicYears: [],
          statuses: [],
          centers: [],
          regions: [],
          municipalities: [],
          schools: []
        },
        model: {
          academicYear: null,
          status: null,
          sieOrSolicitudeOrName: null,
          center: null,
          region: null,
          municipality: null,
          school: null
        }
      },
      pagination: {
        currentPage: 1,
        totalItemCount: 0,
        pageCount: 1,
        count: 0,
        pageSize: DEFAULT_PAGE_SIZE
      },
      items: []
    };
  },
  async mounted() {
    await this.getAcademicYears();
    Promise.all([this.getStatuses(), this.getCenters()]);
  },
  beforeRouteEnter(_, from, next) {
    const ROUTE_NAME_FROM = "StudentsVisualImpairmentsEnterRecord";

    next(async vm => {
      if (from.name == ROUTE_NAME_FROM) {
        vm.recoveryData();
        await vm.goToPage(1, false);
      } else {
        vm.shouldRecovery = false;
      }
    });
  },
  destroyed() {
    this.persistData();
  },
  computed: {
    headerConfig() {
      return {
        icon: "fas fa-user-circle",
        title: "Registro de Estudiantes con Impedimentos Visuales",
        routes: [
          "Inicio",
          "Estudiantes",
          "Registro de Estudiantes con Impedimentos Visuales"
        ]
      };
    },
    headerActionsConfig() {
      return [
        {
          text: "Añadir Estudiante",
          icon: "fas fa-user-plus fa-lg mr-1",
          variant: "outline-success",
          visible: !this.showAddStudent,
          click: () => this.onShowAddStudent()
        }
      ];
    },
    filterConfig() {
      return [
        {
          name: "academicYear",
          component: "CustomDropDown",
          options: this.filter.options.academicYears,
          value: this.filter.model.academicYear,
          placeholder: "Año Académico",
          fieldName: "description",
          fieldKey: "id",
          class: "col-12 col-md-4"
        },
        {
          name: "status",
          component: "CustomDropDown",
          options: this.filter.options.statuses,
          value: this.filter.model.status,
          placeholder: "Estatus",
          fieldName: "name",
          fieldKey: "id",
          class: "col-12 col-md-4"
        },
        {
          name: "sieOrSolicitudeOrName",
          component: "CustomInput",
          placeholder:
            "Escriba Número de SIE, Número de Registro o Nombre del Estudiante",
          value: this.filter.model.sieOrSolicitudeOrName,
          icon: "far fa-search orange-icon fs-lg",
          leftIcon: true,
          class: "col-12 col-md-4",
          maskType: "SieOrSolicitude"
        },
        {
          name: "center",
          component: "CustomDropDown",
          options: this.filter.options.centers,
          value: this.filter.model.center,
          placeholder: "Centro",
          fieldName: "name",
          fieldKey: "id",
          class: "col-12 col-md-3",
          isAdvanced: true,
          onChanged: this.onChangeCenter,
          disabled: this.filter.options.centers.length === 1
        },
        {
          name: "region",
          component: "CustomDropDown",
          options: this.filter.options.regions,
          value: this.filter.model.region,
          placeholder: "Región",
          fieldName: "name",
          fieldKey: "id",
          class: "col-12 col-md-3",
          isAdvanced: true,
          onChanged: this.onChangeRegion,
          disabled:
            !this.filter.model.center?.id ||
            this.filter.options.regions.length === 1
        },
        {
          name: "municipality",
          component: "CustomDropDown",
          options: this.filter.options.municipalities,
          value: this.filter.model.municipality,
          placeholder: "Municipio",
          fieldName: "name",
          fieldKey: "id",
          class: "col-12 col-md-3",
          isAdvanced: true,
          onChanged: this.onChangeMunicipality,
          disabled:
            !this.filter.model.region?.id ||
            this.filter.options.municipalities.length === 1
        },
        {
          name: "school",
          component: "CustomDropDown",
          options: this.filter.options.schools,
          value: this.filter.model.school,
          placeholder: "Escuela",
          fieldName: "name",
          fieldKey: "id",
          class: "col-12 col-md-3",
          isAdvanced: true,
          disabled:
            !this.filter.model.municipality?.id ||
            this.filter.options.schools.length === 1
        }
      ];
    },
    itemsEmpty() {
      return this.items.length === 0;
    },
    showPagination() {
      return this.pagination.totalItemCount > this.pagination.pageSize;
    }
  },
  methods: {
    async getAcademicYears() {
      const { data } = await getVisualImpairmentsAcademicYears();
      this.filter.options.academicYears = data;
      this.filter.model.academicYear = this.currentAcademicYear();
    },
    async getStatuses() {
      const { data } = await getStudentVisualImpairmentStatuses();
      this.filter.options.statuses = data;
    },
    async getCenters() {
      const academicYearId = this.filter.model.academicYear?.id ?? 0;
      const { data } = await locationServices.getCenters({
        academicYearId
      });
      this.filter.options.centers = data;

      if (data.length === 1) {
        this.filter.model.region = null;
        this.filter.options.regions = [];
        this.filter.model.municipality = null;
        this.filter.options.municipalities = [];
        this.filter.model.school = null;
        this.filter.options.schools = [];

        this.filter.model.center = data[0];
        this.getRegions();
      }
    },
    async onChangeCenter() {
      const centerId = this.filter.model.center?.id;
      if (!centerId) {
        this.filter.model.region = null;
        this.filter.options.regions = [];
        this.filter.model.municipality = null;
        this.filter.options.municipalities = [];
        this.filter.model.school = null;
        this.filter.options.schools = [];
        return;
      }
      await this.getRegions();
    },
    async getRegions() {
      const academicYearId = this.filter.model.academicYear?.id ?? 0;
      const centerId = this.filter.model.center.id;
      const { data } = await locationServices.getRegions({
        academicYearId,
        centerId
      });
      this.filter.options.regions = data;

      if (data.length === 1) {
        this.filter.model.municipality = null;
        this.filter.options.municipalities = [];
        this.filter.model.school = null;
        this.filter.options.schools = [];

        this.filter.model.region = data[0];
        this.getMunicipalities();
      }
    },
    async onChangeRegion() {
      const regionId = this.filter.model.region?.id;
      if (!regionId) {
        this.filter.model.municipality = null;
        this.filter.options.municipalities = [];
        this.filter.model.school = null;
        this.filter.options.schools = [];
        return;
      }
      await this.getMunicipalities();
    },
    async getMunicipalities() {
      const academicYearId = this.filter.model.academicYear?.id ?? 0;
      const centerId = this.filter.model.center.id;
      const regionId = this.filter.model.region.id;
      const { data } = await locationServices.getMunicipalities({
        academicYearId,
        centerId,
        regionId
      });
      this.filter.options.municipalities = data;

      if (data.length === 1) {
        this.filter.model.school = null;
        this.filter.options.schools = [];

        this.filter.model.municipality = data[0];
        this.getSchools();
      }
    },
    async onChangeMunicipality() {
      const municipalityId = this.filter.model.municipality?.id;
      if (!municipalityId) {
        this.filter.model.school = null;
        this.filter.options.schools = [];
        return;
      }
      await this.getSchools();
    },
    onShowAddStudent() {
      this.showAddStudent = true;
    },
    async getSchools() {
      const academicYearId = this.filter.model.academicYear?.id ?? 0;
      const centerId = this.filter.model.center.id;
      const regionId = this.filter.model.region.id;
      const municipalityId = this.filter.model.municipality.id;
      const { data } = await locationServices.getSchools({
        academicYearId,
        centerId,
        regionId,
        municipalityId
      });
      this.filter.options.schools = data;

      if (data.length === 1) {
        this.filter.model.school = data[0];
      }
    },
    async goToPage(page) {
      this.pagination.currentPage = page;
      await this.onSearchHandler();
    },
    async filterSearchHandler() {
      this.pagination.currentPage = 1;
      this.onSearchHandler();
    },
    async onSearchHandler() {
      const params = {
        academicYearId:
          this.filter.model.academicYear?.id ?? this.currentAcademicYear().id,
        statusId: this.filter.model.status?.id,
        sieOrSolicitudeOrName: this.filter.model.sieOrSolicitudeOrName,
        centerId: this.filter.model.center?.id,
        regionId: this.filter.model.region?.id,
        municipalityId: this.filter.model.municipality?.id,
        schoolId: this.filter.model.school?.id,
        pageSize: this.pagination.pageSize,
        pageIndex: this.pagination.currentPage
      };
      const { data } = await getVisualImpairments(params);
      this.items = data.data;
      this.pagination = {
        ...this.pagination,
        totalItemCount: data.totalItemCount,
        pageCount: data.pageCount,
        currentPage: data.currentPage,
        count: data.count
      };
    },
    onEnterHandler({ id }) {
      this.$router.push({
        name: "StudentsVisualImpairmentsEnterRecord",
        params: { id }
      });
    },
    onChangeStatusSaved() {
      this.onSearchHandler();
    }
  }
};
</script>

<style scoped lang="scss"></style>
